import PasswordRequirement from "components/PasswordRequirement";
import { routes } from "pages/routes";
import { ChangeEvent, FocusEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLocationState } from "utils/routing";
import type { SignInLocationState } from "./SignIn";
import PasswordInput from "components/ui/PasswordInput";
import Spinner from "components/ui/Spinner";
import FrameBack from "../../images/frame_back.svg?react";
import { verifyPassword } from "../../services/users/auth";
import CheckCircle from "./../../images/check-circle-green.svg?react";
import MainLogo from "./../../images/main-logo.svg?react";
import Button from "components/ui/Button";
import styles from "./PasswordReset.module.scss";
import useSignInForm from "./utils";
import ApiErrorBanner from "components/ApiErrorBanner";

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = useLocationState<SignInLocationState>();
  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    passwordStatus,
    setTouched,
    onValidate,
    onTouched,
  } = useSignInForm({ password: "", confirmPassword: "" });
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<unknown>(null);
  const [resetDone, setResetDone] = useState(false);
  const parsed = new URLSearchParams(location.search);

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isValidated = onValidate();
    setTouched({
      email: true,
      password: true,
    });

    if (!isValidated) {
      return;
    }

    setErrors("");
    setLoading(true);

    try {
      await verifyPassword({
        token: parsed.get("token")!,
        passwd: account.password,
      });
      setResetDone(true);
    } catch (error) {
      setErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locationState?.error) {
      setErrors(locationState.error);
      navigate(location.pathname, { state: null, replace: true });
    }
    // A new location carries the error in its state, and it is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <MainLogo />

        {errors ? (
          <div className={styles.error}>
            <ApiErrorBanner error={errors} />
          </div>
        ) : null}

        {!resetDone && (
          <>
            <div className={styles.header}>
              <h1>{t("Auth.resetForm.title")}</h1>
            </div>

            <p className={styles.description}>
              {t("Auth.passwordReset.enterNewPassword")}
            </p>

            <form onSubmit={e => void handleSubmit(e)}>
              <div className={styles.formWrapper}>
                <PasswordInput
                  label={t("Auth.emailSignup.passwordLabel")}
                  error={touched.password && formErrors.password}
                  size="extra-large"
                  name="password"
                  placeholder={t("Auth.resetForm.passwordPlaceholder")}
                  value={account.password}
                  onChange={handleChangeField}
                  invalid={!!(formErrors.password && touched.password)}
                  onBlur={handleBlurField}
                />
                <PasswordInput
                  label={t("Auth.resetForm.confirmPasswordLabel")}
                  error={touched.confirmPassword && formErrors.confirmPassword}
                  size="extra-large"
                  name="confirmPassword"
                  placeholder={t("Auth.resetForm.passwordPlaceholder")}
                  value={account.confirmPassword}
                  onChange={handleChangeField}
                  invalid={!!(formErrors.confirmPassword && touched.confirmPassword)}
                  onBlur={handleBlurField}
                />

                {account.password && (
                  <div className={styles.passwordRequirement}>
                    <PasswordRequirement passwordStatus={passwordStatus} />
                  </div>
                )}

                {loading ? (
                  <Spinner className={styles.spinner} />
                ) : (
                  <Button className={styles.button} size="large" type="submit">
                    {t("Auth.passwordReset.submit")}
                  </Button>
                )}
              </div>
            </form>
          </>
        )}

        {resetDone && (
          <div className={styles.resetDoneContainer}>
            <CheckCircle />

            <h1 className={styles.resetTitle}>
              {t("Auth.passwordReset.changedHeadline")}
            </h1>

            <p className={styles.resetMessage}>
              {t("Auth.passwordReset.changedMessage")}
            </p>

            <Button
              size="large"
              className={styles.loginButton}
              onClick={() =>
                void navigate(routes.public.signIn.path(), { state: { resetDone: true } })
              }
            >
              {t("Auth.passwordReset.logBackIn")}
            </Button>
          </div>
        )}
      </div>

      {!resetDone && (
        <div>
          <Link className={styles.link} to={routes.public.signIn.path()}>
            ← {t("Auth.actions.backToLogin")}
          </Link>
        </div>
      )}

      <FrameBack className={styles.frameBack} />
    </div>
  );
};

export default PasswordReset;
